import React, { useState, useEffect } from 'react';
import { Plus, Headphones, Edit, Trash2, Play } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import MediaUploader from '../../components/MediaUploader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPodcast = () => {
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', episode_number: '', audio_url: '', external_audio_url: '', date: '', description: '', show_notes: '', transcript: '', tags: [], status: 'draft' });

  const { token } = useAuth();

  const fetchEpisodes = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/cms/podcast`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const data = await res.json(); setEpisodes(data.episodes); }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchEpisodes(); }, []);

  const handleSave = async () => {
    const payload = { ...form, episode_number: form.episode_number ? parseInt(form.episode_number) : null };
    const method = editing ? 'PUT' : 'POST';
    const url = editing ? `${API_URL}/api/cms/podcast/${editing}` : `${API_URL}/api/cms/podcast`;
    await fetch(url, { method, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(payload) });
    setShowForm(false);
    setEditing(null);
    setForm({ title: '', episode_number: '', audio_url: '', external_audio_url: '', date: '', description: '', show_notes: '', transcript: '', tags: [], status: 'draft' });
    fetchEpisodes();
  };

  const handleEdit = (ep) => { setForm({ ...ep, episode_number: ep.episode_number || '' }); setEditing(ep.id); setShowForm(true); };
  const handleDelete = async (id) => { if (!window.confirm('Delete?')) return; await fetch(`${API_URL}/api/cms/podcast/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }); fetchEpisodes(); };

  const inputClass = "w-full bg-navy border border-cream/10 rounded-lg px-3 py-2 text-sm text-cream placeholder-cream/20 focus:outline-none focus:ring-1 focus:ring-gold/30";

  return (
    <div data-testid="admin-podcast">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-serif text-2xl text-cream font-semibold">Podcast</h1><p className="text-cream/30 text-sm mt-1">Episode management</p></div>
        <button onClick={() => { setShowForm(true); setEditing(null); setForm({ title: '', episode_number: '', audio_url: '', external_audio_url: '', date: '', description: '', show_notes: '', transcript: '', tags: [], status: 'draft' }); }} className="inline-flex items-center gap-2 px-4 py-2 bg-gold text-navy text-sm font-semibold rounded-lg hover:bg-gold-light" data-testid="new-episode-btn">
          <Plus size={16} /> New Episode
        </button>
      </div>

      {showForm && (
        <div className="bg-navy border border-gold/[0.06] rounded-xl p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div><label className="block text-[10px] text-cream/20 mb-1 uppercase tracking-wider">Title *</label><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputClass} /></div>
            <div><label className="block text-[10px] text-cream/20 mb-1 uppercase tracking-wider">Episode #</label><input type="number" value={form.episode_number} onChange={(e) => setForm({ ...form, episode_number: e.target.value })} className={inputClass} /></div>
            <div><label className="block text-[10px] text-cream/20 mb-1 uppercase tracking-wider">Audio Upload</label>
              <MediaUploader
                accept="audio"
                folder="podcast"
                label="Upload audio file (MP3, WAV, OGG)"
                onUpload={(url) => setForm(prev => ({ ...prev, audio_url: url }))}
              />
            </div>
            <div><label className="block text-[10px] text-cream/20 mb-1 uppercase tracking-wider">Audio URL</label><input value={form.audio_url || ''} onChange={(e) => setForm({ ...form, audio_url: e.target.value })} className={inputClass} placeholder="Uploaded or paste URL" />
              {form.audio_url && <audio src={form.audio_url} controls className="w-full mt-2" />}
            </div>
            <div><label className="block text-[10px] text-cream/20 mb-1 uppercase tracking-wider">External Audio</label><input value={form.external_audio_url || ''} onChange={(e) => setForm({ ...form, external_audio_url: e.target.value })} className={inputClass} placeholder="External link" /></div>
            <div><label className="block text-[10px] text-cream/20 mb-1 uppercase tracking-wider">Date</label><input type="date" value={form.date || ''} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputClass} /></div>
            <div><label className="block text-[10px] text-cream/20 mb-1 uppercase tracking-wider">Status</label><select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputClass}><option value="draft">Draft</option><option value="published">Published</option></select></div>
          </div>
          <div className="mb-4"><label className="block text-[10px] text-cream/20 mb-1 uppercase tracking-wider">Description</label><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className={`${inputClass} resize-none`} /></div>
          <div className="mb-4"><label className="block text-[10px] text-cream/20 mb-1 uppercase tracking-wider">Transcript</label><textarea value={form.transcript || ''} onChange={(e) => setForm({ ...form, transcript: e.target.value })} rows={4} className={`${inputClass} resize-none`} placeholder="Paste or type transcript..." /></div>
          <div className="flex gap-2">
            <button onClick={handleSave} className="px-4 py-2 bg-gold text-navy text-sm font-semibold rounded-lg hover:bg-gold-light">Save</button>
            <button onClick={() => setShowForm(false)} className="px-4 py-2 text-cream/30 text-sm hover:text-cream/50">Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div className="py-16 text-center text-cream/20 text-sm">Loading...</div> : episodes.length === 0 ? (
        <div className="py-16 text-center bg-navy border border-dashed border-gold/[0.06] rounded-xl">
          <Headphones size={32} className="mx-auto text-cream/10 mb-3" />
          <p className="text-cream/30 text-sm">No episodes yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {episodes.map((ep) => (
            <div key={ep.id} className="bg-navy border border-gold/[0.06] rounded-xl p-4 flex items-center justify-between hover:border-gold/15 transition-all">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-navy-deep rounded-lg flex items-center justify-center"><Headphones size={16} className="text-gold/40" /></div>
                <div>
                  <h3 className="text-cream text-sm font-medium">{ep.episode_number ? `#${ep.episode_number} ` : ''}{ep.title}</h3>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded ${ep.status === 'published' ? 'bg-green-500/10 text-green-400' : 'bg-cream/[0.05] text-cream/30'}`}>{ep.status}</span>
                    {ep.date && <span className="text-cream/15 text-xs">{new Date(ep.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => handleEdit(ep)} className="p-2 text-cream/20 hover:text-gold transition-colors"><Edit size={14} /></button>
                <button onClick={() => handleDelete(ep.id)} className="p-2 text-cream/20 hover:text-red-400 transition-colors"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminPodcast;
